import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getWorkflowsCatalogue, getWorkflowApercu, setWorkflowDestinataires, setModeEssai,
         getWorkflowJournal, rattrapage48h } from '../services/api';

const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('admin_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// ── Petit composant badge statut ──────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    sent:         { bg: '#D1FAE5', color: '#065F46', label: 'Envoyé' },
    already_sent: { bg: '#E5E7EB', color: '#374151', label: 'Déjà envoyé' },
    skipped:      { bg: '#FEF3C7', color: '#92400E', label: 'Ignoré' },
    skipped_sav:  { bg: '#FEF3C7', color: '#92400E', label: 'SAV ignoré' },
    skipped_typo: { bg: '#FEF3C7', color: '#92400E', label: 'Typo exclue' },
    skipped_stage:{ bg: '#FEF3C7', color: '#92400E', label: 'Stage ignoré' },
    error:        { bg: '#FEE2E2', color: '#991B1B', label: 'Erreur' },
    dry_run:      { bg: '#EDE9FE', color: '#5B21B6', label: 'Simulation' },
  };
  const s = map[status] || { bg: '#F3F4F6', color: '#374151', label: status };
  return <span style={{ background: s.bg, color: s.color, borderRadius: 5, padding: '2px 7px', fontSize: 11, fontWeight: 600 }}>{s.label}</span>;
}

// ── Toggle switch ─────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, disabled }) {
  return (
    <div onClick={disabled ? undefined : onChange} style={{ cursor: disabled ? 'not-allowed' : 'pointer', position: 'relative', width: 44, height: 24, flexShrink: 0 }}>
      <div style={{
        width: 44, height: 24, borderRadius: 12,
        background: checked ? '#185FA5' : '#D1D5DB',
        transition: 'background .2s',
      }} />
      <div style={{
        position: 'absolute', top: 3, left: checked ? 23 : 3,
        width: 18, height: 18, borderRadius: '50%', background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,.3)', transition: 'left .2s',
      }} />
    </div>
  );
}

// ── Tag list éditable ─────────────────────────────────────────────────────────
function TagList({ values, onChange }) {
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    if (v && !values.includes(v)) { onChange([...values, v]); setInput(''); }
  };
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {values.map((v, i) => (
          <span key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, background: '#FEE2E2', color: '#991B1B', borderRadius: 6, padding: '3px 8px', fontSize: 12 }}>
            {v}
            <span onClick={() => onChange(values.filter((_, j) => j !== i))} style={{ cursor: 'pointer', fontWeight: 700, marginLeft: 2 }}>×</span>
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <input className="form-input" value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && add()}
          placeholder="Ajouter une valeur…" style={{ fontSize: 12, padding: '4px 8px' }} />
        <button className="btn btn-secondary btn-sm" onClick={add} style={{ whiteSpace: 'nowrap' }}>+ Ajouter</button>
      </div>
    </div>
  );
}

// ── Modal test ────────────────────────────────────────────────────────────────
function TestModal({ onClose, onSent }) {
  const [testEmail, setTestEmail] = useState('cri@valeatelecom.com');
  const [dossierId, setDossierId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const send = async () => {
    setLoading(true); setError('');
    try {
      const res = await api.post('/admin/workflow/notif48h/test', { testEmail, dossierId: dossierId || undefined });
      onSent(`Email de test envoyé à ${testEmail} — dossier : ${res.data.ref || '?'}`);
      onClose();
    } catch (e) { setError(e.response?.data?.error || 'Erreur'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 440 }}>
        <div className="modal-header">
          <div className="modal-title">🧪 Email de test</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} style={{ fontSize: 18 }}>✕</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}
          <div className="form-group">
            <label className="form-label">Email destinataire du test</label>
            <input className="form-input" type="email" value={testEmail} onChange={e => setTestEmail(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Dossier (optionnel)</label>
            <input className="form-input" value={dossierId} onChange={e => setDossierId(e.target.value)} placeholder="Ex: O260622_50939 ou laisser vide" />
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Si vide, le premier dossier Planifiée à venir sera utilisé.</div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Annuler</button>
          <button className="btn btn-primary" onClick={send} disabled={loading || !testEmail}>{loading ? '…' : 'Envoyer'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Panneau d'historique ──────────────────────────────────────────────────────
function HistoryPanel({ runs }) {
  const [expanded, setExpanded] = useState(null);

  if (!runs?.length) return (
    <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text3)', fontSize: 13 }}>
      Aucune exécution enregistrée pour le moment.
    </div>
  );

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      {runs.map((run, i) => (
        <div key={i} style={{ borderBottom: i < runs.length - 1 ? '1px solid var(--border)' : 'none' }}>
          {/* Ligne résumé */}
          <div
            onClick={() => setExpanded(expanded === i ? null : i)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
              background: i % 2 === 0 ? 'var(--bg2)' : 'transparent',
              cursor: run.details?.length ? 'pointer' : 'default',
            }}
          >
            <span style={{ fontSize: 12, color: 'var(--text3)', minWidth: 130 }}>
              {new Date(run.date).toLocaleString('fr-FR')}
            </span>
            <span style={{ fontSize: 12, color: 'var(--text2)', minWidth: 90 }}>
              📅 {run.target_date}
            </span>
            <span style={{ fontSize: 12, flex: 1 }}>
              <strong style={{ color: '#065F46' }}>{run.sent}</strong> envoyé(s)
              {run.errors > 0 && <span style={{ color: '#991B1B' }}> · {run.errors} erreur(s)</span>}
              {run.already_sent > 0 && <span style={{ color: '#374151' }}> · {run.already_sent} déjà traité(s)</span>}
              <span style={{ color: 'var(--text3)' }}> / {run.total} dossier(s)</span>
            </span>
            {run.details?.length > 0 && (
              <span style={{ color: 'var(--text3)', fontSize: 12 }}>{expanded === i ? '▲' : '▼'}</span>
            )}
          </div>
          {/* Détail dossiers */}
          {expanded === i && run.details?.map((d, j) => (
            <div key={j} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '5px 14px 5px 28px',
              background: '#FAFAFA',
              borderTop: '1px solid var(--border)',
              fontSize: 12,
            }}>
              <span style={{ color: 'var(--text3)', fontFamily: 'monospace', minWidth: 120 }}>{d.ref || d.id?.slice(-8) || '—'}</span>
              <StatusBadge status={d.status} />
              {d.typo && <span style={{ color: 'var(--text3)' }}>{d.typo}</span>}
              {d.to && <span style={{ color: 'var(--text3)' }}>{d.to.join(', ')}</span>}
              {d.error && <span style={{ color: '#991B1B' }}>{d.error}</span>}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// ── Panneau de configuration ──────────────────────────────────────────────────
function ConfigPanel({ wf, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ ...wf.config });

  const reset = useCallback(() => { setForm({ ...wf.config }); setEditing(false); setError(''); }, [wf.config]);

  const save = async () => {
    setSaving(true); setError('');
    try {
      await api.put(`/admin/workflow/${wf.id}/config`, form);
      onSaved();
      setEditing(false);
    } catch (e) { setError(e.response?.data?.error || 'Erreur lors de la sauvegarde'); }
    finally { setSaving(false); }
  };

  const cfg = editing ? form : wf.config;

  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--bg2)', borderBottom: '1px solid var(--border)' }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>⚙️ Configuration</span>
        {!editing
          ? <button className="btn btn-secondary btn-sm" onClick={() => { setForm({ ...wf.config }); setEditing(true); }}>✏️ Modifier</button>
          : <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-secondary btn-sm" onClick={reset}>Annuler</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? '…' : 'Enregistrer'}</button>
            </div>
        }
      </div>
      {error && <div className="alert alert-error" style={{ margin: '8px 14px 0' }}>{error}</div>}
      <div style={{ padding: '14px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          {/* Stage requis */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Stage requis</div>
            {editing
              ? <input className="form-input" value={form.stage_requis} onChange={e => setForm(f => ({ ...f, stage_requis: e.target.value }))} style={{ fontSize: 13 }} />
              : <span style={{ fontSize: 13, color: 'var(--text)', background: '#D1FAE5', color: '#065F46', padding: '3px 8px', borderRadius: 6, fontWeight: 600 }}>{cfg.stage_requis}</span>
            }
          </div>
          {/* Champ email client */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Champ email client (Zoho)</div>
            {editing
              ? <input className="form-input" value={form.champ_email_client} onChange={e => setForm(f => ({ ...f, champ_email_client: e.target.value }))} style={{ fontSize: 13 }} />
              : <span style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text2)' }}>{cfg.champ_email_client}</span>
            }
          </div>
          {/* Champ email KOSC */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Champ email KOSC (Zoho)</div>
            {editing
              ? <input className="form-input" value={form.champ_email_kosc} onChange={e => setForm(f => ({ ...f, champ_email_kosc: e.target.value }))} style={{ fontSize: 13 }} />
              : <span style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--text2)' }}>{cfg.champ_email_kosc}</span>
            }
          </div>
        </div>

        {/* Destinataires fixes */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Destinataires fixes (toujours en copie)</div>
          {editing
            ? <TagList values={form.destinataires_fixes} onChange={v => setForm(f => ({ ...f, destinataires_fixes: v }))} />
            : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {cfg.destinataires_fixes?.map((e, i) => (
                  <span key={i} style={{ background: '#EFF6FF', color: '#185FA5', padding: '3px 8px', borderRadius: 6, fontSize: 12, fontWeight: 500 }}>{e}</span>
                ))}
              </div>
          }
        </div>

        {/* Types exclus */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Types de raccordement exclus</div>
          {editing
            ? <TagList values={form.typos_exclus} onChange={v => setForm(f => ({ ...f, typos_exclus: v }))} />
            : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {cfg.typos_exclus?.map((t, i) => (
                  <span key={i} style={{ background: '#FEE2E2', color: '#991B1B', padding: '3px 8px', borderRadius: 6, fontSize: 12 }}>{t}</span>
                ))}
              </div>
          }
        </div>
      </div>
    </div>
  );
}

// ── L'exploitation de la notification 48 h, inchangée ────────
function Notif48h() {
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [running, setRunning] = useState(null);
  const [showTest, setShowTest] = useState(null);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/workflow/status');
      setWorkflows(res.data.workflows || []);
    } catch { setWorkflows([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (wf) => {
    setToggling(wf.id); setError('');
    try {
      const res = await api.post(`/admin/workflow/${wf.id}/toggle`);
      setMsg(`Workflow "${wf.label}" ${res.data.active ? 'activé' : 'désactivé'}.`);
      load();
    } catch (e) { setError(e.response?.data?.error || 'Erreur'); }
    finally { setToggling(null); }
  };

  const runNow = async (wf) => {
    setRunning(wf.id); setError(''); setMsg('');
    try {
      const res = await api.post(`/admin/workflow/notif48h/run`);
      const r = res.data.result;
      if (r.skipped) { setMsg('Workflow désactivé — exécution ignorée.'); }
      else { setMsg(`Terminé — ${r.sent} envoyé(s), ${r.already_sent} déjà traité(s), ${r.errors} erreur(s) sur ${r.total} dossier(s).`); }
      load();
    } catch (e) { setError(e.response?.data?.error || 'Erreur'); }
    finally { setRunning(null); }
  };

  const getTab = (id) => activeTab[id] || 'historique';
  const setTab = (id, tab) => setActiveTab(prev => ({ ...prev, [id]: tab }));

  return (
    <>
      <div>
        {msg && <div className="alert alert-success" onClick={() => setMsg('')} style={{ cursor: 'pointer', marginBottom: 16 }}>{msg} &nbsp;✕</div>}
        {error && <div className="alert alert-error" onClick={() => setError('')} style={{ cursor: 'pointer', marginBottom: 16 }}>{error} &nbsp;✕</div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 48, color: 'var(--text3)' }}>Chargement…</div>
        ) : (
          <>
            {workflows.map(wf => (
              <div key={wf.id} className="card" style={{ marginBottom: 20 }}>

                {/* ── En-tête workflow ── */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: 16 }}>
                  {/* Toggle + infos */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                    <Toggle
                      checked={wf.active}
                      disabled={toggling === wf.id}
                      onChange={() => toggle(wf)}
                    />
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--text)' }}>{wf.label}</span>
                        <span style={{
                          background: wf.active ? '#D1FAE5' : '#F3F4F6',
                          color: wf.active ? '#065F46' : '#9CA3AF',
                          borderRadius: 20, padding: '2px 9px', fontSize: 11, fontWeight: 700,
                        }}>
                          {wf.active ? '● Actif' : '○ Inactif'}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>
                        🕐 {wf.cron_label} &nbsp;·&nbsp; {wf.totalSent} email(s) envoyé(s) au total
                      </div>
                    </div>
                  </div>
                  {/* Actions */}
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowTest(wf.id)}>🧪 Tester</button>
                    <button className="btn btn-primary btn-sm" onClick={() => runNow(wf)} disabled={running === wf.id}>
                      {running === wf.id ? '…' : '▶ Exécuter'}
                    </button>
                  </div>
                </div>

                {/* ── Stats rapides ── */}
                <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                  {[
                    { label: 'Dernière exécution', value: wf.lastRun ? new Date(wf.lastRun.date).toLocaleString('fr-FR') : '—' },
                    { label: 'Date cible', value: wf.lastRun?.target_date || '—' },
                    { label: 'Envoyés (dernière exéc.)', value: wf.lastRun ? `${wf.lastRun.sent} / ${wf.lastRun.total}` : '—', green: true },
                    { label: 'Erreurs (dernière exéc.)', value: wf.lastRun?.errors || 0, red: wf.lastRun?.errors > 0 },
                  ].map((s, i) => (
                    <div key={i} style={{ flex: 1, background: 'var(--bg2)', borderRadius: 8, padding: '8px 12px' }}>
                      <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.4 }}>{s.label}</div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: s.green ? '#065F46' : s.red ? '#991B1B' : 'var(--text)' }}>{s.value}</div>
                    </div>
                  ))}
                </div>

                {/* ── Onglets Config / Historique ── */}
                <div style={{ display: 'flex', gap: 0, marginBottom: 12, borderBottom: '2px solid var(--border)' }}>
                  {[['historique', '📋 Historique'], ['config', '⚙️ Configuration']].map(([tab, label]) => (
                    <button key={tab} onClick={() => setTab(wf.id, tab)} style={{
                      background: 'none', border: 'none', padding: '8px 16px',
                      fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      color: getTab(wf.id) === tab ? '#185FA5' : 'var(--text3)',
                      borderBottom: getTab(wf.id) === tab ? '2px solid #185FA5' : '2px solid transparent',
                      marginBottom: -2,
                    }}>
                      {label}
                    </button>
                  ))}
                </div>

                {getTab(wf.id) === 'historique' && <HistoryPanel runs={wf.recentRuns} />}
                {getTab(wf.id) === 'config' && <ConfigPanel wf={wf} onSaved={() => { load(); setMsg('Configuration enregistrée.'); }} />}
              </div>
            ))}

            {/* Bloc futurs workflows */}
            <div className="card" style={{ background: 'var(--bg2)', border: '1px dashed var(--border)', opacity: 0.8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ fontSize: 22 }}>🔮</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text2)', marginBottom: 2 }}>Prochains workflows</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>Rappel J-1 · Confirmation de RDV · Rapport hebdomadaire client · Notification retard…</div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {showTest && (
        <TestModal
          onClose={() => setShowTest(null)}
          onSent={m => { setMsg(m); setShowTest(null); }}
        />
      )}
    </>
  );
}


// ── Le catalogue des automatisations ────────────────────────────────────────

const COULEUR_TYPE = {
  mail:  ['rgba(24,95,165,.12)', '#185FA5', 'mail'],
  etape: ['rgba(124,90,16,.12)', '#7C5A10', 'étape'],
};

/**
 * L'index des variables d'adresse.
 *
 * Sans lui, un champ destinataire vide ne dit pas qu'on peut y écrire autre
 * chose qu'une adresse en clair.
 */
function IndexVariables({ variables, onInserer }) {
  const [ouvert, setOuvert] = useState(false);
  return (
    <div style={{ marginTop: 10 }}>
      <button type="button" onClick={() => setOuvert(o => !o)}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                 fontSize: 11, fontWeight: 600, color: 'var(--blue2)' }}>
        {ouvert ? '▾' : '▸'} Variables d’adresse disponibles ({variables.length})
      </button>
      {ouvert && (
        <div style={{ marginTop: 8, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          {variables.map(v => (
            <div key={v.jeton} style={{ display: 'flex', gap: 10, alignItems: 'baseline',
                                        padding: '7px 11px', borderBottom: '1px solid var(--border)' }}>
              <code style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 700, color: 'var(--blue2)' }}>{v.jeton}</code>
              <span style={{ fontSize: 12, flex: 1 }}>{v.libelle}</span>
              <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>{v.champ}</span>
              <button type="button" onClick={() => onInserer(v.jeton)}
                style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 5,
                         border: '1px solid var(--border)', background: 'var(--bg3)', cursor: 'pointer' }}>
                ajouter
              </button>
            </div>
          ))}
          <p style={{ margin: 0, padding: '8px 11px', fontSize: 11, color: 'var(--text3)', lineHeight: 1.5 }}>
            Une variable est remplacée au moment de l’envoi par la valeur du dossier. Si le dossier ne la
            porte pas, le destinataire est écarté et l’aperçu le signale. Une adresse en clair fonctionne aussi.
          </p>
        </div>
      )}
    </div>
  );
}

/** Les destinataires d'une automatisation, modifiables. */
function Destinataires({ wf, variables, onEnregistre }) {
  const [liste, setListe] = useState(wf.destinataires);
  const [enCours, setEnCours] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => { setListe(wf.destinataires); setMsg(null); }, [wf.id, wf.destinataires]);

  const modifie = JSON.stringify(liste) !== JSON.stringify(wf.destinataires);

  const enregistrer = async (valeur) => {
    setEnCours(true); setMsg(null);
    try {
      const r = await setWorkflowDestinataires(wf.id, valeur);
      setMsg(['ok', valeur === null ? 'Revenu à la proposition de départ.' : 'Destinataires enregistrés.']);
      onEnregistre(r.workflow);
    } catch (e) {
      setMsg(['ko', e.response?.data?.error || 'Enregistrement impossible.']);
    } finally { setEnCours(false); }
  };

  return (
    <div>
      <div style={{ display: 'grid', gap: 7 }}>
        {liste.map((d, i) => (
          <div key={i} style={{ display: 'flex', gap: 7 }}>
            <input value={d} onChange={e => setListe(l => l.map((x, j) => (j === i ? e.target.value : x)))}
              placeholder="adresse@exemple.fr ou {client.mail}"
              style={{ flex: 1, fontSize: 13, padding: '6px 10px', borderRadius: 7,
                       border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text)',
                       fontFamily: /^\{/.test(d) ? 'var(--mono)' : 'inherit' }} />
            <button type="button" onClick={() => setListe(l => l.filter((_, j) => j !== i))}
              title="retirer"
              style={{ border: '1px solid var(--border)', background: 'var(--bg3)', borderRadius: 7,
                       width: 32, cursor: 'pointer', color: 'var(--text3)' }}>✕</button>
          </div>
        ))}
        {liste.length === 0 && (
          <p style={{ margin: 0, fontSize: 12, color: 'var(--text3)' }}>Aucun destinataire.</p>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button type="button" onClick={() => setListe(l => [...l, ''])}
          style={{ fontSize: 12, fontWeight: 600, padding: '5px 11px', borderRadius: 7,
                   border: '1px solid var(--border)', background: 'var(--bg3)', cursor: 'pointer', color: 'var(--text)' }}>
          + ajouter une ligne
        </button>
        <button type="button" disabled={!modifie || enCours} onClick={() => enregistrer(liste)}
          className="btn btn-primary btn-sm"
          style={{ opacity: !modifie || enCours ? .5 : 1 }}>
          {enCours ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        {wf.personnalise && (
          <button type="button" disabled={enCours} onClick={() => enregistrer(null)}
            style={{ fontSize: 12, padding: '5px 11px', borderRadius: 7, border: '1px solid var(--border)',
                     background: 'transparent', cursor: 'pointer', color: 'var(--text3)' }}>
            revenir au défaut
          </button>
        )}
      </div>

      {msg && (
        <p style={{ margin: '8px 0 0', fontSize: 11, color: msg[0] === 'ok' ? 'var(--green2)' : '#b91c1c' }}>{msg[1]}</p>
      )}

      {wf.personnalise && wf.modifie_le && (
        <p style={{ margin: '8px 0 0', fontSize: 11, color: 'var(--text3)' }}>
          Réglé par {wf.modifie_par} le {new Date(wf.modifie_le).toLocaleString('fr-FR')}.
          Proposition de départ : {wf.destinataires_defaut.join(', ') || 'aucune'}.
        </p>
      )}

      <IndexVariables variables={variables} onInserer={j => setListe(l => [...l, j])} />
    </div>
  );
}

/** L'aperçu du mail tel qu'il partira, sur l'exemple ou sur un vrai dossier. */
function Apercu({ wf }) {
  const [dossier, setDossier] = useState('');
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [chargement, setChargement] = useState(true);
  // Le report de RDV se saisit : sans ces deux champs l'aperçu serait faux.
  const [source, setSource] = useState('Technicien');
  const [cause, setCause] = useState('');

  const charger = useCallback(() => {
    setChargement(true); setErreur(null);
    const p = {};
    if (dossier.trim()) p.dossier = dossier.trim();
    if (wf.saisie) { p.source = source; p.cause = cause; }
    getWorkflowApercu(wf.id, p)
      .then(setData)
      .catch(e => setErreur(e.response?.data?.error || 'Aperçu impossible'))
      .finally(() => setChargement(false));
  }, [wf.id, wf.saisie, dossier, source, cause]);

  useEffect(() => { charger(); }, [charger]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <input value={dossier} onChange={e => setDossier(e.target.value)}
          placeholder="ID Zoho d’un dossier (facultatif)"
          style={{ fontSize: 12, padding: '6px 10px', borderRadius: 7, border: '1px solid var(--border)',
                   background: 'var(--bg)', color: 'var(--text)', fontFamily: 'var(--mono)', width: 230 }} />
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>
          {data && data.source === 'exemple' ? 'rendu sur un dossier d’exemple' : data ? 'rendu sur ' + data.source : ''}
        </span>
      </div>

      {wf.saisie && (
        <div style={{ display: 'grid', gap: 9, marginBottom: 14, padding: '12px 14px', background: 'var(--bg)',
                      border: '1px solid var(--border)', borderRadius: 9 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>Source du report</div>
            <select value={source} onChange={e => setSource(e.target.value)}
              style={{ fontSize: 13, padding: '6px 10px', borderRadius: 7, border: '1px solid var(--border)',
                       background: 'var(--bg2)', color: 'var(--text)' }}>
              <option>Technicien</option>
              <option>Client</option>
            </select>
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 3 }}>Cause du report</div>
            <textarea value={cause} onChange={e => setCause(e.target.value)} rows={2}
              placeholder="Texte libre, saisi au moment du report"
              style={{ width: '100%', fontSize: 13, padding: '6px 10px', borderRadius: 7, resize: 'vertical',
                       border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)' }} />
          </div>
        </div>
      )}

      {chargement && <p style={{ fontSize: 12, color: 'var(--text3)' }}>Rendu du mail…</p>}
      {erreur && <p style={{ fontSize: 12, color: '#b91c1c' }}>{erreur}</p>}

      {data && data.sans_apercu && (
        <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--text2)', background: 'var(--bg)',
                    border: '1px solid var(--border)', borderRadius: 8, padding: '11px 13px' }}>{data.message}</p>
      )}

      {data && data.sujet !== undefined && (
        <>
          <div style={{ display: 'grid', gap: 5, marginBottom: 11, fontSize: 12 }}>
            <div><span style={{ color: 'var(--text3)' }}>À : </span>
              {data.destinataires.length
                ? data.destinataires.join(', ')
                : <span style={{ color: '#b91c1c' }}>aucun destinataire résolu</span>}</div>
            <div><span style={{ color: 'var(--text3)' }}>Objet : </span><strong>{data.sujet}</strong></div>
          </div>
          {data.manquants.length > 0 && (
            <p style={{ margin: '0 0 11px', fontSize: 11, lineHeight: 1.5, color: '#7C5A10',
                        background: 'rgba(219,139,0,.1)', border: '1px solid rgba(219,139,0,.3)',
                        borderRadius: 8, padding: '8px 11px' }}>
              Écartés faute de valeur sur ce dossier : {data.manquants.map(m => m.libelle + ' (' + m.jeton + ')').join(', ')}.
            </p>
          )}
          {data.note_apercu && (
            <p style={{ margin: '0 0 11px', fontSize: 11, lineHeight: 1.5, color: 'var(--text2)',
                        background: 'var(--bg)', border: '1px solid var(--border)',
                        borderRadius: 8, padding: '8px 11px' }}>{data.note_apercu}</p>
          )}
          <iframe title="aperçu du mail" srcDoc={data.corps}
            style={{ width: '100%', height: 520, border: '1px solid var(--border)', borderRadius: 9, background: '#fff' }} />
        </>
      )}
    </div>
  );
}

/**
 * L'historique d'une automatisation.
 *
 * Le 8 octobre, 23 mails de prevenance sont tombes sur une erreur de
 * certificat et personne ne l'a su : le journal existait, mais nulle part a
 * l'ecran. Les echecs sont donc montres en premier, et en rouge.
 */
function Journal({ wf }) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [dateRattrapage, setDateRattrapage] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [msg, setMsg] = useState(null);

  const charger = useCallback(() => {
    setErreur(null);
    getWorkflowJournal(wf.id).then(setD).catch(e => setErreur(e.response?.data?.error || 'Lecture impossible'));
  }, [wf.id]);

  useEffect(() => { charger(); }, [charger]);

  const rattraper = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateRattrapage)) {
      setMsg(['ko', 'Date attendue au format AAAA-MM-JJ.']); return;
    }
    if (!window.confirm(
      `Renvoyer la prévenance pour les interventions du ${dateRattrapage} ?\n\n`
      + 'De vrais mails partiront chez de vrais clients. Les dossiers déjà prévenus seront ignorés.')) return;
    setEnCours(true); setMsg(null);
    try {
      const r = await rattrapage48h(dateRattrapage);
      setMsg(['ok', `Passe terminée : ${r.run.sent} envoyé(s), ${r.run.already_sent} déjà prévenu(s), ${r.run.errors} en échec.`]);
      charger();
    } catch (e) {
      setMsg(['ko', e.response?.data?.error || 'Rattrapage impossible.']);
    } finally { setEnCours(false); }
  };

  if (erreur) return <p style={{ fontSize: 12, color: '#b91c1c' }}>{erreur}</p>;
  if (!d) return <p style={{ fontSize: 12, color: 'var(--text3)' }}>Lecture du journal…</p>;

  if (d.forme === 'aucun' || (d.forme === 'envois' && !d.envois.length)
      || (d.forme === 'bascules' && !d.bascules.length)) {
    return (
      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.5, color: 'var(--text2)', background: 'var(--bg)',
                  border: '1px solid var(--border)', borderRadius: 8, padding: '11px 13px' }}>{d.message}</p>
    );
  }

  if (d.forme === 'envois') {
    const sim = d.envois.filter(e => e.simulation).length;
    return (
      <div>
        {sim > 0 && (
          <p style={{ margin: '0 0 12px', fontSize: 12, lineHeight: 1.5, color: 'var(--text2)',
                      background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8,
                      padding: '9px 12px' }}>
            {sim} entrée(s) en <strong>simulation</strong> : c’est ce que nous aurions envoyé, au moment
            où Zoho a envoyé pour de vrai. Rien n’est parti d’ici. Comparez les destinataires avec ce que
            Covage et les clients ont reçu avant de nous laisser prendre la main.
          </p>
        )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text3)' }}>
              <th style={{ padding: '6px 10px' }}>Quand</th>
              <th style={{ padding: '6px 10px' }}>Dossier</th>
              <th style={{ padding: '6px 10px' }}>Destinataires</th>
              <th style={{ padding: '6px 10px' }}>Issue</th>
            </tr>
          </thead>
          <tbody>
            {d.envois.map((e, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{new Date(e.at).toLocaleString('fr-FR')}</td>
                <td style={{ padding: '6px 10px', fontFamily: 'var(--mono)' }}>{e.ref_work_order || e.ref_commande || '—'}</td>
                <td style={{ padding: '6px 10px' }}>{(e.to || []).join(', ')}</td>
                <td style={{ padding: '6px 10px', color: e.simulation ? 'var(--text3)'
                                     : (e.success ? 'var(--green2)' : '#b91c1c') }}>
                  {e.simulation
                    ? (e.success ? 'simulation' : 'simulation — ' + e.error)
                    : (e.success ? 'envoyé' : (e.error || 'échec'))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      </div>
    );
  }

  // Les bascules d'etape : l'escalade dit ce qu'elle a change, et sur quel
  // dossier. Sans ca, une etape qui bouge toute seule reste inexplicable.
  if (d.forme === 'bascules') {
    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text3)' }}>
              <th style={{ padding: '6px 10px' }}>Quand</th>
              <th style={{ padding: '6px 10px' }}>Work order</th>
              <th style={{ padding: '6px 10px' }}>Intervention du</th>
              <th style={{ padding: '6px 10px' }}>Technicien</th>
              <th style={{ padding: '6px 10px' }}>Bascule</th>
            </tr>
          </thead>
          <tbody>
            {d.bascules.map((b, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--border)',
                                   background: b.ok === false ? 'rgba(185,28,28,.06)' : 'transparent' }}>
                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{new Date(b.at).toLocaleString('fr-FR')}</td>
                <td style={{ padding: '6px 10px', fontFamily: 'var(--mono)' }}>{b.ref_work_order || '—'}</td>
                <td style={{ padding: '6px 10px' }}>{b.date_intervention}</td>
                <td style={{ padding: '6px 10px' }}>{b.technicien || '—'}</td>
                <td style={{ padding: '6px 10px' }}>
                  {b.depuis} → <strong>{b.vers}</strong>
                  {b.simulation && <span style={{ color: 'var(--text3)' }}> (simulation)</span>}
                  {b.ok === false && <span style={{ color: '#b91c1c' }}> — {b.erreur}</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Les passes de la prévenance 48 h.
  const enEchec = d.passes.filter(p => p.erreurs > 0);
  return (
    <div>
      {enEchec.length > 0 && (
        <div style={{ marginBottom: 16, padding: '12px 14px', borderRadius: 9,
                      background: 'rgba(185,28,28,.08)', border: '1px solid rgba(185,28,28,.35)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#b91c1c', marginBottom: 7 }}>
            {enEchec.length} passe(s) avec des mails non envoyés
          </div>
          {enEchec.slice(0, 5).map((p, i) => (
            <div key={i} style={{ fontSize: 12, lineHeight: 1.6, marginBottom: 7 }}>
              <strong>{new Date(p.at).toLocaleDateString('fr-FR')}</strong> — {p.erreurs} échec(s)
              sur les interventions du {p.date_visee || '?'} :{' '}
              <span style={{ fontFamily: 'var(--mono)', fontSize: 11 }}>
                {p.echecs.slice(0, 8).map(e => e.ref).join(', ')}
                {p.echecs.length > 8 ? ` (+${p.echecs.length - 8})` : ''}
              </span>
              {p.echecs[0] && (
                <div style={{ fontSize: 11, color: 'var(--text3)' }}>cause : {p.echecs[0].erreur}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14,
                    padding: '11px 13px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 9 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Rattraper une date :</span>
        <input value={dateRattrapage} onChange={e => setDateRattrapage(e.target.value)} placeholder="AAAA-MM-JJ"
          style={{ fontSize: 12, padding: '5px 9px', borderRadius: 7, border: '1px solid var(--border)',
                   background: 'var(--bg2)', color: 'var(--text)', fontFamily: 'var(--mono)', width: 120 }} />
        <button type="button" disabled={enCours} onClick={rattraper} className="btn btn-primary btn-sm">
          {enCours ? 'Envoi…' : 'Renvoyer'}
        </button>
        <span style={{ fontSize: 11, color: 'var(--text3)' }}>
          Les dossiers déjà prévenus sont ignorés.
        </span>
      </div>
      {msg && <p style={{ margin: '0 0 12px', fontSize: 12, color: msg[0] === 'ok' ? 'var(--green2)' : '#b91c1c' }}>{msg[1]}</p>}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: 'var(--text3)' }}>
              <th style={{ padding: '6px 10px' }}>Passe</th>
              <th style={{ padding: '6px 10px' }}>Interventions du</th>
              <th style={{ padding: '6px 10px', textAlign: 'right' }}>Vus</th>
              <th style={{ padding: '6px 10px', textAlign: 'right' }}>Envoyés</th>
              <th style={{ padding: '6px 10px', textAlign: 'right' }}>Déjà</th>
              <th style={{ padding: '6px 10px', textAlign: 'right' }}>Échecs</th>
            </tr>
          </thead>
          <tbody>
            {d.passes.map((p, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--border)',
                                   background: p.erreurs ? 'rgba(185,28,28,.06)' : 'transparent' }}>
                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{new Date(p.at).toLocaleString('fr-FR')}</td>
                <td style={{ padding: '6px 10px' }}>
                  {p.date_visee || <span style={{ color: 'var(--text3)' }}>{p.raison === 'jour_non_ouvre' ? 'jour non ouvré' : '—'}</span>}
                </td>
                <td style={{ padding: '6px 10px', textAlign: 'right' }}>{p.total}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 600 }}>{p.envoyes}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--text3)' }}>{p.deja_envoyes}</td>
                <td style={{ padding: '6px 10px', textAlign: 'right', fontWeight: 700,
                             color: p.erreurs ? '#b91c1c' : 'var(--text3)' }}>{p.erreurs}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--text3)' }}>
        {d.total_memorise} dossiers prévenus depuis la mise en service. Une passe qui échoue déclenche
        désormais une alerte WhatsApp vers les numéros des alertes SAV.
      </p>
    </div>
  );
}

function Catalogue() {
  const [liste, setListe] = useState([]);
  const [variables, setVariables] = useState([]);
  const [choisi, setChoisi] = useState(null);
  const [onglet, setOnglet] = useState('apercu');
  const [chargement, setChargement] = useState(true);
  const [modeEssai, poserModeEssai] = useState(null);

  useEffect(() => {
    getWorkflowsCatalogue()
      .then(d => {
        setListe(d.workflows);
        setVariables(d.variables);
        setChoisi(c => c || (d.workflows[0] && d.workflows[0].id));
        poserModeEssai(d.mode_essai || { actif: false });
      })
      .catch(() => setListe([]))
      .finally(() => setChargement(false));
  }, []);

  const wf = liste.find(w => w.id === choisi) || null;

  // Un mail deja en service n'a pas d'apercu : son onglet d'accueil est son
  // historique, qui est justement ce qu'on vient y chercher.
  useEffect(() => {
    if (wf && wf.type === 'mail' && !wf.template && onglet === 'apercu') setOnglet('journal');
  }, [wf, onglet]);

  if (chargement) return <div style={{ padding: 30, color: 'var(--text3)' }}>Chargement du catalogue…</div>;

  return (
    <>
    <ModeEssai etat={modeEssai} onChange={poserModeEssai} />
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>

      {/* La liste */}
      <aside style={{ flex: '1 1 300px', minWidth: 0, maxWidth: 380, background: 'var(--bg2)',
                      border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
        {liste.map(w => {
          const [fond, couleur, label] = COULEUR_TYPE[w.type] || ['var(--bg3)', 'var(--text3)', w.type];
          const actif = w.id === choisi;
          return (
            <button key={w.id} type="button" onClick={() => setChoisi(w.id)}
              style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                       padding: '11px 14px', border: 'none', borderBottom: '1px solid var(--border)',
                       background: actif ? 'var(--bg3)' : 'transparent',
                       boxShadow: actif ? 'inset 3px 0 0 var(--accent)' : 'none' }}>
              <div style={{ display: 'flex', gap: 7, alignItems: 'center', marginBottom: 3, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 9,
                               background: fond, color: couleur }}>{label}</span>
                {w.enService && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 9,
                                 background: 'rgba(46,160,67,.14)', color: 'var(--green2)' }}>en service</span>
                )}
                {w.personnalise && (
                  <span style={{ fontSize: 10, color: 'var(--text3)' }}>réglé</span>
                )}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{w.nom}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2, lineHeight: 1.4 }}>{w.portee}</div>
            </button>
          );
        })}
      </aside>

      {/* Le détail */}
      <main style={{ flex: '999 1 540px', minWidth: 0, background: 'var(--bg2)',
                     border: '1px solid var(--border)', borderRadius: 10 }}>
        {!wf ? (
          <div style={{ padding: 30, color: 'var(--text3)' }}>Choisissez une automatisation.</div>
        ) : (
          <>
            <div style={{ padding: '15px 18px', borderBottom: '1px solid var(--border)' }}>
              <h2 style={{ margin: '0 0 7px', fontSize: 16 }}>{wf.nom}</h2>
              <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>
                <div><strong>Déclencheur</strong> — {wf.declencheur}</div>
                <div><strong>Règle Zoho d’origine</strong> — {wf.zoho}</div>
              </div>
              {wf.note && (
                <p style={{ margin: '10px 0 0', fontSize: 12, lineHeight: 1.5, color: '#7C5A10',
                            background: 'rgba(219,139,0,.1)', border: '1px solid rgba(219,139,0,.3)',
                            borderRadius: 8, padding: '9px 12px' }}>{wf.note}</p>
              )}
            </div>

            {wf.type === 'mail' ? (
              <>
                <div role="tablist" style={{ display: 'flex', gap: 2, padding: '6px 8px 0',
                                             borderBottom: '1px solid var(--border)' }}>
                  {[...(wf.template ? [['apercu', 'Aperçu du mail']] : []),
                    ['destinataires', `Destinataires (${wf.destinataires.length})`],
                    ['journal', 'Historique']]
                    .map(([k, nom]) => (
                    <button key={k} type="button" role="tab" aria-selected={onglet === k} onClick={() => setOnglet(k)}
                      style={{ fontSize: 12, fontWeight: 600, padding: '8px 15px', border: 'none', cursor: 'pointer',
                               borderRadius: '7px 7px 0 0',
                               background: onglet === k ? 'var(--bg)' : 'transparent',
                               color: onglet === k ? 'var(--text)' : 'var(--text3)',
                               boxShadow: onglet === k ? 'inset 0 -2px 0 var(--accent)' : 'none' }}>{nom}</button>
                  ))}
                </div>
                <div style={{ padding: '16px 18px' }}>
                  {onglet === 'apercu' && wf.template && <Apercu wf={wf} />}
                  {onglet === 'destinataires' && (
                    <Destinataires wf={wf} variables={variables}
                      onEnregistre={w => setListe(l => l.map(x => (x.id === w.id ? w : x)))} />
                  )}
                  {onglet === 'journal' && <Journal wf={wf} />}
                  {onglet === 'apercu' && !wf.template && <Journal wf={wf} />}
                </div>
              </>
            ) : (
              <div style={{ padding: '16px 18px' }}>
                <p style={{ margin: '0 0 14px', fontSize: 13, lineHeight: 1.6, color: 'var(--text2)' }}>
                  Cette automatisation ne produit pas de mail : elle change l’étape du dossier.
                  Le délai est dans le code pour qu’il reste vérifiable.
                </p>
                <Journal wf={wf} />
              </div>
            )}
          </>
        )}
      </main>
    </div>
    </>
  );
}

/**
 * Le mode essai : détourner tous les envois vers une seule adresse.
 *
 * Il se réglait dans une variable d'environnement sur le serveur, donc il
 * fallait un accès SSH et un redémarrage pour armer un essai — et se souvenir
 * de le désarmer. C'est un bouton, parce que c'est une décision du moment.
 *
 * Son bandeau est volontairement voyant quand il est armé : oublier qu'il
 * l'est ferait croire qu'un mail est parti chez Covage alors qu'il dort dans
 * une boîte d'essai.
 */
function ModeEssai({ etat, onChange }) {
  const [adresse, setAdresse] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  useEffect(() => { setAdresse((etat && etat.adresse) || 'cri@valeatelecom.com'); },
    [etat && etat.adresse]);

  if (!etat) return null;
  const force = etat.source === 'environnement';

  const basculer = async (actif) => {
    setEnCours(true); setErreur(null);
    try {
      const r = await setModeEssai(actif, adresse.trim());
      onChange(r.mode_essai);
    } catch (e) {
      setErreur(e.response?.data?.error || 'Changement impossible.');
    } finally { setEnCours(false); }
  };

  return (
    <div style={{
      marginBottom: 16, padding: '12px 15px', borderRadius: 10,
      background: etat.actif ? 'rgba(217,119,6,.12)' : 'var(--bg2)',
      border: '1px solid ' + (etat.actif ? 'rgba(217,119,6,.45)' : 'var(--border)'),
    }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: etat.actif ? '#92400E' : 'var(--text)' }}>
          {etat.actif ? '⚠ Mode essai armé' : 'Mode essai'}
        </span>
        {etat.actif ? (
          <span style={{ fontSize: 12, color: 'var(--text2)' }}>
            Tout envoi part à <strong>{etat.adresse}</strong> et nulle part ailleurs.
            Ni Covage, ni les clients, ni les techniciens ne reçoivent rien.
          </span>
        ) : (
          <span style={{ fontSize: 12, color: 'var(--text3)' }}>
            Désarmé : les mails partent à leurs vrais destinataires.
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
        <input type="email" value={adresse} disabled={enCours || force || etat.actif}
          onChange={e => setAdresse(e.target.value)} placeholder="adresse de détournement"
          style={{ fontSize: 13, padding: '6px 9px', borderRadius: 7, minWidth: 240,
                   border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)' }} />
        {etat.actif ? (
          <button type="button" disabled={enCours || force} onClick={() => basculer(false)}
            style={{ fontSize: 13, fontWeight: 700, padding: '7px 14px', borderRadius: 8,
                     cursor: force ? 'not-allowed' : 'pointer', border: '1px solid var(--border)',
                     background: 'var(--bg3)', color: 'var(--text)' }}>
            {enCours ? '…' : 'Désarmer — revenir aux vrais destinataires'}
          </button>
        ) : (
          <button type="button" disabled={enCours || !adresse.trim()} onClick={() => basculer(true)}
            style={{ fontSize: 13, fontWeight: 700, padding: '7px 14px', borderRadius: 8,
                     cursor: 'pointer', border: '1px solid rgba(217,119,6,.5)',
                     background: 'rgba(217,119,6,.15)', color: '#92400E' }}>
            {enCours ? '…' : 'Armer le mode essai'}
          </button>
        )}
        {force && (
          <span style={{ fontSize: 11, color: '#92400E' }}>
            Posé par <code>MAILS_TEST_VERS</code> sur le serveur : ce bouton ne peut pas le lever.
          </span>
        )}
        {etat.le && !force && (
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>
            dernier changement le {new Date(etat.le).toLocaleString('fr-FR')} par {etat.par || '—'}
          </span>
        )}
        {erreur && <span style={{ fontSize: 11, color: '#b91c1c' }}>{erreur}</span>}
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function Workflow() {
  const [vue, setVue] = useState('catalogue');

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Workflows</div>
      </div>

      <div className="page">
        <div role="tablist" style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[['catalogue', 'Catalogue des automatisations'], ['notif48h', 'Notification 48 h']].map(([k, nom]) => (
            <button key={k} type="button" role="tab" aria-selected={vue === k} onClick={() => setVue(k)}
              style={{ fontSize: 13, fontWeight: 600, padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                       border: '1px solid ' + (vue === k ? 'var(--accent)' : 'var(--border)'),
                       background: vue === k ? 'var(--bg2)' : 'transparent',
                       color: vue === k ? 'var(--text)' : 'var(--text3)' }}>{nom}</button>
          ))}
        </div>

        <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text3)', lineHeight: 1.6, maxWidth: 760 }}>
          Les huit automatisations qu’on reprend à Zoho : six mails et deux escalades d’étape.
          <strong> Rien ne part encore d’ici</strong> — on règle les destinataires et on vérifie les
          textes, puis on passera en simulation pour comparer avec ce que Zoho envoie réellement.
        </p>

        {vue === 'catalogue' ? <Catalogue /> : <Notif48h />}
      </div>
    </>
  );
}

